import { cn, Skeleton, Tag } from '@atlas/ui-primitives'
import { useState } from 'react'
import { CardShell } from '../card-shell/card-shell'
import { AxisMultiSelect, AxisMultiSelectSkeleton } from './axis-multi-select'
import type { FilterAxis, FilterSelection } from './types'

/** What the rail adds to the shared card surface, so it and its skeleton match. */
const RAIL_CLASS = 'space-y-5'
const RAIL_TITLE_CLASS = 'text-ds-xs leading-4 font-medium uppercase tracking-widest text-ds-text-subtle'
/** The "Add variable" affordance's frame, shared so the skeleton reserves its height too. */
const ADD_VARIABLE_CLASS =
	'rounded-ds-md border border-dashed border-ds-border px-3 py-2 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring'

export interface FilterRailProps {
	/** All pickable axes (from the registry). */
	axes: FilterAxis[]
	/** Axis codes currently shown as groups. */
	shownAxisCodes: string[]
	/** Current per-axis value selection. */
	selection: FilterSelection
	/** Toggle a value on an axis (select if absent, deselect if present). */
	onToggle: (axisCode: string, valueCode: string) => void
	/** Reveal a hidden axis as a new group. */
	onAddAxis: (axisCode: string) => void
	/** Optional heading label (default "Filter rail"). */
	title?: string
	/** Optional remove affordance for dynamically-added groups — additive; existing consumers omit both new props. */
	onRemoveAxis?: (axisCode: string) => void
	/** Axis codes eligible for the remove affordance (a fixed group's code should never appear here). */
	removableAxisCodes?: string[]
}

/**
 * Filter rail — one multi-select dropdown per shown axis; hidden axes are revealed
 * via "Add variable". Presentational and data-driven: renders whatever axes/values
 * it is given. Design-system molecule (promoted from the Library page) shared by
 * any feature that filters by classification axes.
 */
export function FilterRail({
	axes,
	shownAxisCodes,
	selection,
	onToggle,
	onAddAxis,
	title = 'Filter rail',
	onRemoveAxis,
	removableAxisCodes,
}: FilterRailProps) {
	const [adding, setAdding] = useState(false)
	const shown = axes.filter((a) => shownAxisCodes.includes(a.code))
	const hidden = axes.filter((a) => !shownAxisCodes.includes(a.code))

	return (
		<CardShell as="aside" className={RAIL_CLASS}>
			<p className={RAIL_TITLE_CLASS}>{title}</p>
			{shown.map((axis) => (
				<AxisMultiSelect
					key={axis.code}
					axis={axis}
					selectedCodes={selection[axis.code] ?? []}
					onToggle={(valueCode) => onToggle(axis.code, valueCode)}
					onRemove={onRemoveAxis && removableAxisCodes?.includes(axis.code) ? () => onRemoveAxis(axis.code) : undefined}
				/>
			))}

			{hidden.length > 0 && (
				<div className="border-t border-dashed border-ds-border pt-4">
					<button
						type="button"
						onClick={() => setAdding((v) => !v)}
						className={cn(
							ADD_VARIABLE_CLASS,
							'w-full text-ds-sm leading-5 text-ds-text-muted hover:border-ds-text-subtle hover:text-ds-text',
						)}
					>
						+ Add variable
						<span className="block text-ds-xs leading-4 text-ds-text-subtle">dimension or attribute</span>
					</button>
					{adding && (
						<div className="mt-2 flex flex-wrap gap-1.5">
							{hidden.map((axis) => (
								<button
									key={axis.code}
									type="button"
									className="rounded-ds-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
									onClick={() => {
										onAddAxis(axis.code)
										setAdding(false)
									}}
								>
									<Tag variant="primary" size="sm">
										{axis.label}
									</Tag>
								</button>
							))}
						</div>
					)}
				</div>
			)}
		</CardShell>
	)
}

export interface FilterRailSkeletonProps {
	/**
	 * The heading the loaded rail will show. Static text the caller already holds, so a grey
	 * bar here would hide what the page knows and then resize when it arrives.
	 */
	title?: string
	/** How many axis groups to stand in for — pass the number the rail will open with. */
	groupCount?: number
	/**
	 * Names what is loading for a reader who cannot see the pulse; visually hidden. Omitted
	 * when an enclosing region already says it, so nothing is announced twice.
	 */
	label?: string
}

/**
 * The rail before its axes are known: the same box, the same heading, and a placeholder per
 * group it is about to show, so the page's side column has its width and height from the
 * first frame and the controls land where the placeholders stood.
 *
 * Lives beside the rail rather than in each feature, so a rail and its own loading state
 * cannot drift apart — and so no feature has to restate the rail's chrome to stand in for it.
 */
export function FilterRailSkeleton({ title = 'Filter rail', groupCount = 3, label }: FilterRailSkeletonProps) {
	return (
		<CardShell as="aside" className={RAIL_CLASS} aria-busy="true">
			{label ? <span className="sr-only">{label}</span> : null}
			<p className={RAIL_TITLE_CLASS}>{title}</p>
			{Array.from({ length: groupCount }, (_, i) => (
				<AxisMultiSelectSkeleton key={i} />
			))}
			{/* The registry always has more axes than the rail opens with, so the affordance to
			    reveal them is part of the shape the rail arrives in. */}
			<div className="border-t border-dashed border-ds-border pt-4">
				<div className={ADD_VARIABLE_CLASS}>
					<Skeleton shape="text" className="mx-auto h-5 w-28" />
					<Skeleton shape="text" className="mx-auto mt-0.5 h-4 w-36" />
				</div>
			</div>
		</CardShell>
	)
}
